import React, { useRef, useState, useEffect } from 'react'
import { Regions, useWindowSize, useConfig } from 'msp-integrations'
import {
  getAccordionStyles,
  ImageLeftStyle,
  ImageRightStyle,
  getAccordionItemsStyles,
  DynamicColors
} from './Accordion.styles'
import { AccordionProps } from './Accordion.types'
import AccordionItem from './AccordionItem'
import BaseAccordion from './BaseAccordion'
import Icons from '@atoms/Icons/Icons'
import getBackgroundColor from '../../../types/BackgroundColor'
import Image from '@atoms/Image/Image'

const Accordion = ({
  defaultOpenAccordionImage,
  fallbackImage,
  fallbackImageAlt,
  defaultOpenAccordionItemId,
  idToImageMapping,
  bgStyle,
  accordionItems,
  fullWidth,
  accordionHeadline,
  imagePosition
}: AccordionProps) => {
  const {
    theme: {
      font: { regular: font, wide: fontWide }
    }
  } = useConfig()
  const backgroundColor = getBackgroundColor(bgStyle)
  const dynamicColors =
    DynamicColors[bgStyle as keyof typeof DynamicColors] ??
    DynamicColors.default
  const AccordionStyles = getAccordionStyles(dynamicColors)
  const AccordionItemsStyles = getAccordionItemsStyles(dynamicColors)
  const [mainImageSrc, setMainImageSrc] = useState(
    defaultOpenAccordionImage || fallbackImage
  )
  const [imgAlt, setImgAlt] = useState(fallbackImageAlt || accordionHeadline)
  const [openItemId, setOpenItemId] = useState(defaultOpenAccordionItemId)
  const accordionRef = useRef<HTMLDivElement>(null)
  const [accordionHeight, setAccordionHeight] = useState<number>()
  const refDesktopImage = useRef(null)
  const { width, breakpoints } = useWindowSize()
  const isDesktop = width && width >= breakpoints.md
  const handleItemClick = (itemId: string, imgAlt?: string) => {
    itemId === openItemId ? setOpenItemId('') : setOpenItemId(itemId)
    if (isDesktop) {
      setMainImageSrc(idToImageMapping.get(itemId))
      imgAlt && setImgAlt(imgAlt)
      handleHeight()
    }
  }
  const handleHeight = () => {
    if (
      !accordionHeight ||
      (accordionRef.current?.clientHeight ?? 0) > accordionHeight
    ) {
      setAccordionHeight(accordionRef.current?.clientHeight)
    }
  }
  useEffect(() => {
    handleHeight()
  }, [])

  return (
    <div
      className={backgroundColor}
      ref={accordionRef}
      style={{ minHeight: accordionHeight + 'px' }}
    >
      <BaseAccordion
        wrapperStyles={`${AccordionStyles.wrapperStyles} ${backgroundColor}`}
      >
        <div className={`hidden ${fullWidth ? '' : 'lg:block'}`}>
          <div
            id={'mainImageWrapper'}
            className={
              imagePosition == 'right'
                ? ImageRightStyle.mainImageWrapper
                : ImageLeftStyle.mainImageWrapper
            }
          >
            <Image
              innerRef={refDesktopImage}
              src={mainImageSrc}
              alt={imgAlt}
              ratio='1/2'
              loading='eager'
              width='100%'
              height='auto'
            />
          </div>
        </div>
        <div
          className={
            fullWidth
              ? AccordionStyles.accordionItemsWrapper
              : imagePosition == 'right'
              ? ImageRightStyle.accordionItemsWrapper
              : ImageLeftStyle.accordionItemsWrapper
          }
        >
          {accordionHeadline && (
            <div className={AccordionStyles.headlineWrapper}>
              {accordionHeadline}
            </div>
          )}
          {accordionItems.map((row: any, index: number) => {
            const buttonProps = {
              buttonStyle: `${AccordionItemsStyles.buttonStyle} ${fontWide} ${backgroundColor}`,
              openButtonStyle: `${AccordionItemsStyles.openButtonStyle} ${fontWide} ${backgroundColor}`,
              buttonText: [
                <div
                  key={`accordion-item-header-${row.id}`}
                  className={'flex w-fit flex-row'}
                >
                  {row.headlineIndex && (
                    <div className={'pr-4'}>{row.headlineIndex}</div>
                  )}
                  <div className={'break-text pr-2'}>{row.headline}</div>
                </div>
              ],
              openIcon: [
                <div
                  key={`accordion-open-${row.id}-${index}`}
                  className={dynamicColors.arrow.open}
                >
                  <Icons iconName={'ArrowDownFilled'} size='sm' />
                </div>
              ],
              closeIcon: [
                <div
                  key={`accordion-close-${row.id}-${index}`}
                  className={dynamicColors.arrow.close}
                >
                  <Icons iconName={'ArrowUpFilled'} size='sm' />
                </div>
              ]
            }
            const panelProps = {
              panelStyle: `${AccordionItemsStyles.panelStyle} ${font}`,
              openPanelStyle: `${AccordionItemsStyles.openPanelStyle} ${font}`,
              transitionsProps: {
                enter: 'transition duration-500 ease-in-out transform',
                enterFrom: 'transform scale-95 opacity-0',
                enterTo: 'transform scale-100 opacity-100',
                leave: 'transition duration-400 ease-in-out transform',
                leaveFrom: 'transform scale-100 opacity-100',
                leaveTo: 'transform scale-95 opacity-0',
                show: isDesktop ? openItemId === row.id : undefined
              }
            }

            const image = row?.imagePath || fallbackImage
            return (
              <div
                key={`accordion-component-${row.id}-${index}`}
                id={row.id}
                onClick={() => handleItemClick(row.id, row.imageAlt)}
                className={'w-full'}
              >
                <AccordionItem
                  buttonProps={buttonProps}
                  panelProps={panelProps}
                  isOpen={openItemId === row.id}
                  multipleOpen={!isDesktop}
                  indexKey={index}
                >
                  {row?.regions && <Regions regions={row.regions}></Regions>}
                  {row?.contentText && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: row.contentText.replace(/\r\n/g, '<br/>')
                      }}
                    />
                  )}
                  {!fullWidth && (
                    <div className='block lg:hidden'>
                      <div className={AccordionItemsStyles.mobileImageWrapper}>
                        <Image
                          className={'accordionItemImage'}
                          src={image}
                          alt={row.imageAlt || fallbackImageAlt || row.headline}
                          ratio='1/2'
                          width='100%'
                          height='auto'
                        />
                      </div>
                    </div>
                  )}
                </AccordionItem>
              </div>
            )
          })}
          <div className={AccordionItemsStyles.separatorWrapper}>
            <div className={AccordionItemsStyles.separator}></div>
          </div>
        </div>
        <div className='clear-both' />
      </BaseAccordion>
    </div>
  )
}

export default Accordion
